import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Modal, Button } from 'react-bootstrap';
import { notify } from 'react-notify-toast';
import moment from 'moment';
import 'moment/locale/es';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faReceipt,
  faMoneyBillWave,
  faCoins,
  faChartPie,
  faPrint,
  faEdit,
  faTrashAlt,
  faCalendarAlt,
  faEnvelope,
  faChalkboardTeacher,
  faCreditCard,
  faExclamationTriangle,
  faInbox,
  faUserTie,
  faTag,
  faCheckCircle,
  faClock,
} from '@fortawesome/free-solid-svg-icons';

import Searchbox from './Searchbox';
import EditBill from './EditBill';
import Pagin from '../Pagin';
import LoadingScreen from '../../Layout/LoadingScreen';
import clientAxios from '../../clients/clientAxios';
import './listBill.css';

const DEFAULT_LAST_DAYS = 7;

const isFullPayment = (bill) => bill?.fullPaid === true;
const isPartialPayment = (bill) => bill?.partial === true && !isFullPayment(bill);

const ListBill = () => {
  const history = useHistory();

  const [fullBills, setFullBills] = useState([]);
  const [listedBills, setListedBills] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [deleteModalShow, setDeleteModalShow] = useState(false);
  const [billToDelete, setBillToDelete] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [selectedBillId, setSelectedBillId] = useState('');

  const fetchInitialBills = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await clientAxios.post('/bills/search', {
        dateFrom: moment()
          .subtract(DEFAULT_LAST_DAYS, 'days')
          .format('YYYY-MM-DD'),
      });
      setFullBills(response.data || []);
    } catch (error) {
      notify.show(error?.message || 'Error al obtener comprobantes', 'error');
      history.push('/users/login');
    } finally {
      setIsLoading(false);
    }
  }, [history]);

  useEffect(() => {
    fetchInitialBills();
  }, [fetchInitialBills]);

  const getBills = async () => {
    try {
      setIsDataLoading(true);
      const response = await clientAxios.get('/bills/get');
      setFullBills(response.data || []);
    } catch (error) {
      notify.show(error?.message || 'Error al obtener boletas', 'error');
      history.push('/users/login');
    } finally {
      setIsDataLoading(false);
    }
  };

  const handleChangePage = (newListedBills) => {
    setListedBills(newListedBills || []);
  };

  const handleOpenDeleteModal = (bill) => {
    setBillToDelete(bill);
    setDeleteModalShow(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalShow(false);
    setBillToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!billToDelete?._id) return;
    try {
      await clientAxios.delete(`/bills/delete/${billToDelete._id}`);
      handleCloseDeleteModal();
      await getBills();
      notify.show('Boleta eliminada exitosamente', 'success');
    } catch (error) {
      notify.show('Ha ocurrido un error al eliminar la boleta', 'error');
    }
  };

  const handleOpenEdit = (billId) => {
    setSelectedBillId(billId);
    setShowEditForm(true);
  };

  const handleCloseEdit = () => {
    setShowEditForm(false);
    setSelectedBillId('');
  };

  const metrics = useMemo(() => {
    const totalCount = fullBills.length;
    const getBillAmount = (bill) => Number(bill.amount) || 0;
    const totalAmount = fullBills.reduce((acc, bill) => acc + getBillAmount(bill), 0);
    const fullCount = fullBills.filter(isFullPayment).length;
    const partialCount = fullBills.filter(isPartialPayment).length;
    const fullPaidAmount = fullBills.reduce(
      (acc, bill) => (isFullPayment(bill) ? acc + getBillAmount(bill) : acc),
      0
    );
    const installmentAmount = totalAmount - fullPaidAmount;
    const averageAmount = totalCount > 0 ? Math.round(totalAmount / totalCount) : 0;

    return {
      totalCount,
      totalAmount,
      fullCount,
      partialCount,
      fullPaidAmount,
      installmentAmount,
      averageAmount,
    };
  }, [fullBills]);

  const getPaymentBadgeClass = (paymentMethod) => {
    const normalized = (paymentMethod || '').toLowerCase();
    if (normalized.includes('mercado') || normalized.includes('mp')) {
      return 'payment-method-mercadopago';
    }
    if (normalized.includes('efectivo')) {
      return 'payment-method-efectivo';
    }
    if (normalized.includes('banco') || normalized.includes('transferencia')) {
      return 'payment-method-banco';
    }
    if (normalized.includes('tarjeta')) {
      return 'payment-method-tarjetas';
    }
    if (normalized.includes('paypal')) {
      return 'payment-method-paypal';
    }
    return 'payment-method-default';
  };

  const getStudentInitials = (name) => {
    if (!name || typeof name !== 'string') return 'AL';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <div className='list-bill-container'>
      <div className='list-bill-header'>
        <div className='list-bill-title-box'>
          <h2>
            <span className='list-bill-title-icon'>
              <FontAwesomeIcon icon={faReceipt} />
            </span>
            Listado de Boletas y Pagos
          </h2>
          <p>Consulta, filtra y gestiona los comprobantes y cobranzas emitidas</p>
        </div>
      </div>

      <div className='list-bill-stats-grid'>
        <div className='list-bill-stat-card'>
          <div className='list-bill-stat-icon blue'>
            <FontAwesomeIcon icon={faReceipt} />
          </div>
          <div className='list-bill-stat-info'>
            <span className='list-bill-stat-value'>{metrics.totalCount}</span>
            <span className='list-bill-stat-label'>Total Boletas</span>
          </div>
        </div>

        <div className='list-bill-stat-card'>
          <div className='list-bill-stat-icon emerald'>
            <FontAwesomeIcon icon={faMoneyBillWave} />
          </div>
          <div className='list-bill-stat-info'>
            <span className='list-bill-stat-value'>
              $ {metrics.totalAmount.toLocaleString('es-AR')}
            </span>
            <span className='list-bill-stat-label'>Total Recaudado</span>
            <span className='list-bill-stat-breakdown'>
              <span className='list-bill-stat-breakdown-row'>
                <span className='list-bill-stat-breakdown-dot installment' />
                Cuotas: $ {metrics.installmentAmount.toLocaleString('es-AR')}
              </span>
              <span className='list-bill-stat-breakdown-row'>
                <span className='list-bill-stat-breakdown-dot fullpaid' />
                Pagos totales: $ {metrics.fullPaidAmount.toLocaleString('es-AR')}
              </span>
            </span>
          </div>
        </div>

        <div className='list-bill-stat-card'>
          <div className='list-bill-stat-icon amber'>
            <FontAwesomeIcon icon={faCoins} />
          </div>
          <div className='list-bill-stat-info'>
            <span className='list-bill-stat-value'>{metrics.partialCount}</span>
            <span className='list-bill-stat-label'>Pagos Parciales</span>
          </div>
        </div>

        <div className='list-bill-stat-card'>
          <div className='list-bill-stat-icon purple'>
            <FontAwesomeIcon icon={faChartPie} />
          </div>
          <div className='list-bill-stat-info'>
            <span className='list-bill-stat-value'>
              $ {metrics.averageAmount.toLocaleString('es-AR')}
            </span>
            <span className='list-bill-stat-label'>Promedio x Pago</span>
          </div>
        </div>
      </div>

      <Searchbox
        defaultLastDays={DEFAULT_LAST_DAYS}
        refresh={getBills}
        setResults={(newResults) => setFullBills(newResults || [])}
        setIsDataLoading={(loading) => setIsDataLoading(loading)}
      />

      <div className='list-bill-results-header'>
        <span className='list-bill-count-badge'>
          <FontAwesomeIcon icon={faReceipt} />
          {metrics.totalCount} {metrics.totalCount === 1 ? 'boleta encontrada' : 'boletas encontradas'}
        </span>
      </div>

      {isDataLoading ? (
        <LoadingScreen />
      ) : fullBills.length === 0 ? (
        <div className='list-bill-empty'>
          <FontAwesomeIcon icon={faInbox} className='list-bill-empty-icon' />
          <h4 className='list-bill-empty-title'>No se encontraron comprobantes</h4>
          <p className='list-bill-empty-text'>
            No hay boletas que coincidan con los filtros aplicados o dentro del rango de fechas seleccionado.
          </p>
          <button
            type='button'
            className='btn-header-action'
            onClick={getBills}
          >
            Ver todas las boletas
          </button>
        </div>
      ) : (
        <div className='list-bill-cards-wrapper'>
          {listedBills.map((bill) => {
            const formattedDate = bill.date
              ? moment(bill.date).utc().format('L')
              : 'N/A';
            const formattedCreatedAt = bill.createdAt
              ? moment(bill.createdAt).endOf('day').format('L')
              : 'N/A';
            const creatorName =
              typeof bill.createdBy === 'object' && bill.createdBy !== null
                ? bill.createdBy.name || 'Sistema'
                : bill.createdBy || 'Sistema';
            const numericAmount = Number(bill.amount) || 0;
            const fullPayment = isFullPayment(bill);
            const partialPayment = isPartialPayment(bill);

            return (
              <div
                key={bill._id}
                className={`bill-item-card ${partialPayment ? 'is-partial' : ''} ${fullPayment ? 'is-full' : ''}`}
              >
                <div className='bill-card-main'>
                  <div className='bill-student-section'>
                    <div className='bill-meta-header'>
                      <span className='bill-number-pill' title='Número de Boleta'>
                        <FontAwesomeIcon icon={faTag} />
                        #{bill.billNumber || 'S/N'}
                      </span>

                      {fullPayment ? (
                        <span className='bill-badge-full'>
                          <FontAwesomeIcon icon={faCheckCircle} className='mr-1' />
                          Pago Completo
                        </span>
                      ) : partialPayment ? (
                        <span className='bill-badge-partial'>
                          <FontAwesomeIcon icon={faClock} className='mr-1' />
                          Pago Parcial
                        </span>
                      ) : null}

                      {bill.monthlyInstallment && (
                        <span className='bill-badge-installment'>
                          Cuota {bill.monthlyInstallment}
                        </span>
                      )}

                      {bill.classroom && (
                        <span className='bill-badge-classroom' title={bill.classroom}>
                          <FontAwesomeIcon icon={faChalkboardTeacher} className='mr-1' />
                          {bill.classroom}
                        </span>
                      )}

                      <span
                        className={`bill-payment-method ${getPaymentBadgeClass(
                          bill.payment
                        )}`}
                      >
                        <FontAwesomeIcon icon={faCreditCard} />
                        {bill.payment || 'Otro'}
                      </span>
                    </div>

                    <div className='bill-student-info mt-2'>
                      <div className='bill-student-avatar'>
                        {getStudentInitials(bill.nameDisplay)}
                      </div>
                      <div className='bill-student-text'>
                        <h4 className='bill-student-name' title={bill.nameDisplay}>
                          {bill.nameDisplay || 'Alumno Sin Nombre'}
                        </h4>
                        <span className='bill-student-email' title={bill.email}>
                          <FontAwesomeIcon icon={faEnvelope} className='mr-1' />
                          {bill.email || 'Sin correo electrónico'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className='bill-amount-actions'>
                    <div className='bill-amount-box'>
                      <span className='bill-amount-label'>Monto Abonado</span>
                      <span className='bill-amount-number'>
                        $ {numericAmount.toLocaleString('es-AR')}
                      </span>
                    </div>

                    <div className='bill-actions-row'>
                      <Link
                        to={`/bills/print/${bill._id}`}
                        className='btn-bill-action print'
                        title='Imprimir Boleta'
                      >
                        <FontAwesomeIcon icon={faPrint} />
                      </Link>

                      <button
                        type='button'
                        className='btn-bill-action edit'
                        title='Editar Boleta'
                        onClick={() => handleOpenEdit(bill._id)}
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>

                      <button
                        type='button'
                        className='btn-bill-action delete'
                        title='Eliminar Boleta'
                        onClick={() => handleOpenDeleteModal(bill)}
                      >
                        <FontAwesomeIcon icon={faTrashAlt} />
                      </button>
                    </div>
                  </div>
                </div>

                <div className='bill-details-grid'>
                  <div className='bill-detail-item'>
                    <span className='bill-detail-label'>
                      <FontAwesomeIcon icon={faCalendarAlt} className='mr-1' /> Fecha de Pago
                    </span>
                    <span className='bill-detail-value'>
                      <strong>{formattedDate}</strong>
                    </span>
                  </div>

                  <div className='bill-detail-item'>
                    <span className='bill-detail-label'>
                      <FontAwesomeIcon icon={faClock} className='mr-1' /> Fecha de Emisión
                    </span>
                    <span className='bill-detail-value'>{formattedCreatedAt}</span>
                  </div>

                  <div className='bill-detail-item'>
                    <span className='bill-detail-label'>
                      <FontAwesomeIcon icon={faUserTie} className='mr-1' /> Emitido Por
                    </span>
                    <span className='bill-detail-value'>{creatorName}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {fullBills.length > 0 && !isDataLoading && (
        <div className='list-bill-pagination-wrapper'>
          <Pagin items={fullBills} onChangePage={handleChangePage} />
        </div>
      )}

      <EditBill
        show={showEditForm}
        onHide={handleCloseEdit}
        id={selectedBillId}
        refresh={getBills}
      />

      <Modal
        show={deleteModalShow}
        onHide={handleCloseDeleteModal}
        centered
        className='delete-bill-modal'
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon
              icon={faExclamationTriangle}
              className='text-warning mr-2'
            />
            Confirmar Eliminación
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className='text-light mb-2'>
            ¿Está seguro de que desea eliminar la boleta{' '}
            <strong>#{billToDelete?.billNumber || ''}</strong> perteneciente al alumno{' '}
            <strong>{billToDelete?.nameDisplay || ''}</strong>?
          </p>
          <div className='delete-warning-box'>
            <FontAwesomeIcon icon={faExclamationTriangle} className='mr-2 text-warning' />
            <strong>Atención:</strong> Si elimina esta boleta, tendrá que reflejar los cambios
            manualmente en la caja chica.
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={handleCloseDeleteModal}>
            Cancelar
          </Button>
          <Button variant='danger' onClick={handleConfirmDelete}>
            <FontAwesomeIcon icon={faTrashAlt} className='mr-1' /> Eliminar Boleta
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ListBill;
